import TFeedbackDefinition, {
    TFeedbackCheckbox,
    TFeedbackComponent,
    TFeedbackLabel,
    TFeedbackRadio, TFeedbackTextarea
} from "./TFeedbackDefinition";
import m, {Vnode, VnodeDOM} from "mithril";
import FeedbackModel from "./FeedbackModel";
import StarsCell from "../../customForms/shared/components/FormCell/StarsCell";
import {Questionary} from "../questionary";
import h from "../../ui/design";

export default class FeedbackFactory {

    private readonly componentInstances: {[variableName: string]: StarsCell} = {};

    constructor(
        private readonly definition: TFeedbackDefinition,
        private readonly model: FeedbackModel,
        private readonly questionary: Questionary,
    ) { }

    public getHeaderDefinitions(): TFeedbackComponent[] {
        const result: TFeedbackComponent[] = [];
        const first = this.definition.form.components[0];
        const second = this.definition.form.components[1];
        if (first?.component === "label" && first.type === "question") {
            result.push(first);
            if (second?.component === "label" && second.type === "hint") {
                result.push(second);
            }
        }

        return result;
    }

    public getFormDefinitions(): TFeedbackComponent[] {
        const skipCount = this.getHeaderDefinitions().length;
        return this.definition.form.components.slice(skipCount);
    }

    public createComponentView(definition: TFeedbackComponent): Vnode|null {
        switch (definition.component) {
            case "label":
                return this.createLabelView(definition);
            case "radio":
                return this.createRadioView(definition);
            case "checkbox":
                return this.createCheckboxView(definition);
            case "textarea":
                return this.createTextareaView(definition);
        }

        // @ts-ignore Do something if component is not valid
        console.error(`Cannot create feedback component '${definition.component}'.`)
        // @ts-ignore return empty element
        return null;
    }

    private createLabelView(definition: TFeedbackLabel): Vnode|null {
        switch (definition.type) {
            case "label": return this.createLabelLabelView(definition);
            case "hint": return this.createLabelHintView(definition);
            case "question": return this.createLabelQuestionView(definition);
        }
        console.error(`Cannot create feedback component label '${definition.type}'.`)
        return null;
    }

    private createLabelLabelView(definition: TFeedbackLabel): Vnode {
        return m('.labelCell', {}, [
            m('label', {}, m.trust(this.questionary.renderText(definition.label))),
        ])
    }

    private createLabelHintView(definition: TFeedbackLabel): Vnode {
        return m('.hint', {}, [
            m('p', {}, m.trust(this.questionary.renderText(definition.label))),
        ])
    }

    private createLabelQuestionView(definition: TFeedbackLabel): Vnode {
        return m('.question', {}, [
            m('p.question-text', {}, m.trust(this.questionary.renderText(definition.label))),
        ])
    }

    private createRadioView(definition: TFeedbackRadio): Vnode|null {
        switch (definition.type) {
            case "radio": return this.createRadioRadioView(definition);
            case "stars": return this.createRadioStarsView(definition);
        }
        console.error(`Cannot create feedback component radio '${definition.type}'.`);
        return null;
    }

    private createRadioRadioView(definition: TFeedbackRadio): Vnode {
        const currentValue = this.model.get(definition.variable);

        return m('.radioCellGroup', {}, [
            definition.label ? this.createLabelLabelView({component: "label", type: "label", label: definition.label}) : null,
            ...definition.values.map(radio => {
                const checked = currentValue === radio.value;
                return m(".radioCell"+(checked?".checked":''), {
                        onclick: () => this.model.set(definition.variable, radio.value),
                    }, [
                        m("label",
                            [
                                m(`input`, {
                                    id: `radio_${definition.variable}`,
                                    type: 'radio',
                                    name: definition.variable,
                                    value: radio.value,
                                    checked: checked,
                                    tabindex: 1,
                                    onchange: () => this.model.set(definition.variable, radio.value),
                                }),
                                m("span"+(checked ? h.backgroundSecondary : ''),
                                    m(".option-label", m.trust(this.questionary.renderText(radio.label ?? String(radio.value))))
                                ),
                            ]
                        ),
                    ]
                );
            })
       ]);
    }

    private createRadioStarsView(definition: TFeedbackRadio): Vnode {
        if (!this.componentInstances[definition.variable]) {

            this.componentInstances[definition.variable] = new StarsCell(
                definition.values.length,
                definition.values.length,
                () => {
                    const variableValue = this.model.get(definition.variable);
                    let numericValue = null;
                    definition.values.some((v,i) => {
                        if (v.value === variableValue) {
                            numericValue = i;
                            return true;
                        }
                    });
                    return typeof numericValue === "number" ? definition.values[numericValue].value : null;
                },
                numericValue => {
                    this.model.set(definition.variable, definition.values[numericValue-1].value);
                },
            )
        }

        const labels = definition.values.map(v => v.label)
            .filter(l => l)
            .map(l => m('label', {}, m.trust(this.questionary.renderText(l!))));

        return m('.starsCellGroup', {}, [
            m(this.componentInstances[definition.variable]),
            m('.labels', {}, labels)
        ]);
    }

    private createCheckboxView(definition: TFeedbackCheckbox): Vnode {
        const checked = this.model.get(definition.variable);
        return m(".checkboxCell",
            m("label",
                [
                    m("input", {
                        id: `checkbox_${definition.variable}`,
                        type: "checkbox",
                        tabindex: 1,
                        name: definition.variable,
                        onchange: () => this.model.set(definition.variable, !checked),
                        checked: checked,
                    }),
                    m("span"+(checked ? h.backgroundSecondary : ''),
                        m(".option-label", {}, m.trust(this.questionary.renderText(definition.label)))
                    )
                ]
            )
        )
    }

    private createTextareaView(definition: TFeedbackTextarea): Vnode {
        return m('.textAreaCellGroup', {}, [
            definition.label ? this.createLabelLabelView({component: "label", type: "label", label: definition.label}) : null,
            m(".TextareaCell",
                m("textarea",  {
                    id: `textarea_${definition.variable}`,
                    tabIndex: 1,
                    autocomplete: "off",
                    name: definition.variable,
                    oncreate: (vnode: VnodeDOM) => (<HTMLTextAreaElement>vnode.dom).value = String(this.model.get(definition.variable) ?? ''),
                    oninput: (e: InputEvent) => this.model.set(definition.variable, (<HTMLTextAreaElement>e.currentTarget).value),
                }),
            )
        ]);
    }

}
